// ─── SandboxAnimacion: el iframe de una animación por código, del lado del editor ─
// Reemplaza a `AnimationLayer` cuando el clip trae `animationCodigo` en vez de
// una plantilla. Monta `/sandbox/animacion.html` en un iframe con
// `sandbox="allow-scripts"` (ver `documentoSandbox.ts`), le manda el código y
// las props, y le sigue el playhead con `ir_a` — igual que `AnimationLayer` hace
// `seekTo` sobre el Player, pero del otro lado de la frontera.
//
// Las herramientas del agente necesitan dos cosas de este componente desde
// afuera de React: saber si el código compiló (`listo` / `error`) y pedir
// capturas. Para eso cada instancia se anota en `sandboxes` por clipId mientras
// está montada, y `esperarSandbox` / `capturarSandbox` (abajo) hablan con ella.

import React, { useEffect, useMemo, useRef } from 'react';
import { ANIMATION_FPS } from './types';
import { RUTA_SANDBOX_ANIMACION, SANDBOX_ANIMACION } from './sandbox/documentoSandbox';
import {
  CANAL,
  esMensajeDelSandbox,
  type MensajeAlSandbox,
  type MensajeDelSandbox,
  type PropsDeAnimacion,
} from './sandbox/protocolo';

type Estado =
  | { tipo: 'cargando' }
  | { tipo: 'listo' }
  | { tipo: 'error'; mensaje: string; etapa: string };

type Instancia = {
  estado: Estado;
  esperando: Array<(e: Estado) => void>;
  capturas: Array<(imagenes: string[]) => void>;
  enviar: (m: MensajeAlSandbox) => void;
};

/** Las instancias montadas, por clipId. */
const sandboxes = new Map<string, Instancia>();

/** Cuánto se espera a que un sandbox diga `listo` o `error`. */
export const TIMEOUT_SANDBOX_MS = 15_000;

/**
 * Espera a que el sandbox del clip compile: resuelve con `listo` o con el error.
 * Si el clip no está montado (no está bajo el playhead) o no responde, resuelve
 * con un error que lo dice.
 *
 * El tope vale SIEMPRE, también después de colgarse de la lista de espera: hasta
 * el 2026-09-26 sólo corría mientras la instancia no existía, y un sandbox que
 * nunca decía `listo` ni `error` dejaba esta promesa abierta para siempre: la
 * herramienta no volvía, el candado de "un cambio por vez" quedaba tomado y el
 * agente rebotaba con "ya hay un cambio en curso" sin que hubiera nada corriendo
 * (ADR-2026-09-26-el-error-del-player-llega-al-agente).
 */
export function esperarSandbox(clipId: string, timeoutMs = TIMEOUT_SANDBOX_MS): Promise<Estado> {
  return new Promise((resolve) => {
    const inicio = Date.now();
    const sinRespuesta = (inst: Instancia | undefined): Estado => ({
      tipo: 'error',
      mensaje: inst
        ? 'El sandbox de la animación no respondió a tiempo.'
        : 'La animación no está montada en el preview (el playhead tiene que estar sobre el clip).',
      etapa: 'render',
    });
    const intentar = () => {
      const inst = sandboxes.get(clipId);
      if (inst && inst.estado.tipo !== 'cargando') return resolve(inst.estado);
      if (Date.now() - inicio > timeoutMs) return resolve(sinRespuesta(inst));
      if (inst) {
        const restante = Math.max(0, timeoutMs - (Date.now() - inicio));
        const alCambiar = (e: Estado) => {
          clearTimeout(reloj);
          resolve(e);
        };
        const reloj = setTimeout(() => {
          const i = inst.esperando.indexOf(alCambiar);
          if (i >= 0) inst.esperando.splice(i, 1);
          resolve(sinRespuesta(inst));
        }, restante);
        inst.esperando.push(alCambiar);
        // Por si el estado cambió entre el get y el push.
        if (inst.estado.tipo !== 'cargando') {
          clearTimeout(reloj);
          const i = inst.esperando.indexOf(alCambiar);
          if (i >= 0) inst.esperando.splice(i, 1);
          return resolve(inst.estado);
        }
        return;
      }
      setTimeout(intentar, 100);
    };
    intentar();
  });
}

/**
 * Marca el sandbox del clip como "cargando" ANTES de cambiarle el código con
 * `setClips`: el estado de React llega un render después, y sin esto
 * `esperarSandbox` vería el `listo` de la versión anterior y devolvería
 * capturas viejas.
 */
export function reiniciarSandbox(clipId: string): void {
  const inst = sandboxes.get(clipId);
  if (inst) inst.estado = { tipo: 'cargando' };
}

/** Pide N frames al sandbox del clip y devuelve las imágenes como data URLs PNG. */
export function capturarSandbox(
  clipId: string,
  frames: number[],
  anchoPx = 640,
  timeoutMs = TIMEOUT_SANDBOX_MS,
  jpeg = false,
): Promise<string[]> {
  return new Promise((resolve) => {
    const inst = sandboxes.get(clipId);
    if (!inst || inst.estado.tipo !== 'listo') return resolve([]);
    const t = setTimeout(() => resolve([]), timeoutMs);
    inst.capturas.push((imgs) => {
      clearTimeout(t);
      resolve(imgs);
    });
    inst.enviar({ canal: CANAL, tipo: 'capturar', clipId, frames, anchoPx, jpeg });
  });
}

type Props = {
  clipId: string;
  codigo: string;
  props: PropsDeAnimacion;
  /** Segundos dentro del clip. */
  localTime: number;
  durationSec: number;
  width: number;
  height: number;
};

export const SandboxAnimacion: React.FC<Props> = ({
  clipId,
  codigo,
  props,
  localTime,
  durationSec,
  width,
  height,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const cargadoRef = useRef(false);
  const durationInFrames = Math.max(1, Math.round(durationSec * ANIMATION_FPS));
  const propsJson = JSON.stringify(props);

  const enviar = useMemo(
    () => (m: MensajeAlSandbox) => {
      const w = iframeRef.current?.contentWindow;
      if (!w) return;
      // `'*'`: el iframe vive en un origen opaco, no hay otro targetOrigin posible.
      w.postMessage(m, '*');
    },
    [],
  );

  const montar = useMemo(
    () => () =>
      enviar({
        canal: CANAL,
        tipo: 'montar',
        clipId,
        codigo,
        props: JSON.parse(propsJson) as PropsDeAnimacion,
        fps: ANIMATION_FPS,
        duracionEnFrames: durationInFrames,
        ancho: width,
        alto: height,
      }),
    [enviar, clipId, codigo, propsJson, durationInFrames, width, height],
  );

  // `montar` cambia con el código; el listener (abajo) se registra una sola vez
  // por clip y lo lee por ref, así la instancia de `sandboxes` sobrevive a un
  // cambio de código. Si se recreara, un `esperarSandbox` que ya estaba
  // esperando en la instancia vieja no se enteraría nunca del `listo`.
  const montarRef = useRef(montar);
  montarRef.current = montar;

  // Registro de la instancia + escucha de mensajes del iframe. Depende SÓLO del
  // clipId: ver arriba.
  useEffect(() => {
    const inst: Instancia = { estado: { tipo: 'cargando' }, esperando: [], capturas: [], enviar };
    sandboxes.set(clipId, inst);
    const cambiar = (e: Estado) => {
      inst.estado = e;
      const cola = inst.esperando.splice(0);
      for (const f of cola) f(e);
    };
    const onMensaje = (ev: MessageEvent) => {
      // Sólo lo que viene de ESTE iframe y con nuestra forma.
      if (ev.source !== iframeRef.current?.contentWindow) return;
      if (!esMensajeDelSandbox(ev.data)) return;
      const m: MensajeDelSandbox = ev.data;
      if (m.tipo === 'cargado') {
        cargadoRef.current = true;
        montarRef.current();
        return;
      }
      if (m.clipId !== clipId) return;
      if (m.tipo === 'listo') cambiar({ tipo: 'listo' });
      else if (m.tipo === 'error') cambiar({ tipo: 'error', mensaje: m.mensaje, etapa: m.etapa });
      else if (m.tipo === 'capturas') {
        const cola = inst.capturas.splice(0);
        for (const f of cola) f(m.imagenes);
      }
    };
    window.addEventListener('message', onMensaje);
    return () => {
      window.removeEventListener('message', onMensaje);
      if (sandboxes.get(clipId) === inst) sandboxes.delete(clipId);
    };
  }, [clipId, enviar]);

  // Código, props o tamaño nuevos con el iframe ya cargado: se vuelve a montar.
  useEffect(() => {
    if (!cargadoRef.current) return;
    const inst = sandboxes.get(clipId);
    if (inst) inst.estado = { tipo: 'cargando' };
    montar();
  }, [montar, clipId]);

  // El playhead del editor manda el frame.
  useEffect(() => {
    if (!cargadoRef.current) return;
    enviar({
      canal: CANAL,
      tipo: 'ir_a',
      clipId,
      frame: Math.max(0, Math.min(durationInFrames - 1, Math.round(localTime * ANIMATION_FPS))),
    });
  }, [enviar, clipId, localTime, durationInFrames]);

  return (
    <iframe
      ref={iframeRef}
      title="Animación"
      src={RUTA_SANDBOX_ANIMACION}
      sandbox={SANDBOX_ANIMACION}
      // Sin permisos extra; y sin `allow` de nada.
      referrerPolicy="no-referrer"
      style={{
        width: '100%',
        height: '100%',
        border: 0,
        display: 'block',
        background: 'transparent',
        colorScheme: 'normal',
        pointerEvents: 'none',
      }}
    />
  );
};
